import { useMemo, useState } from "react";
import { useGetProjectHistory } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.queries";
import { ProjectHistoryApiError } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.repository";
import { HistoryEvent } from "src/domains/myPageContent/projectHistory/history-query/projectHistory.model";
import { useHistoryProjectSelection } from "./useHistoryProjectSelection";
import { useHasUnresolvedProjectSwitch } from "./useHasUnresolvedProjectSwitch";

export type { ProjectOption } from "./useHistoryProjectSelection";

/**
 * 마이페이지 "프로젝트 히스토리" 패널 훅.
 *
 * - 판정 로직 두 가지는 하위 훅에 위임한다 — 프로젝트 옵션·기본 선택은
 *   `useHistoryProjectSelection`, 기준 프로젝트 대비 미해소 전환 판정은
 *   `useHasUnresolvedProjectSwitch` 가 담당한다. 이 훅은 두 결과를 그대로 반환에 실어
 *   보내고, 필터 상태·히스토리 조회·`flatItems` 조립·오류 분류만 직접 다룬다.
 * - `historyTypeFilter`: `null` 이 "전체"(API 파라미터 생략)를 의미한다.
 * - `useGetProjectHistory` 는 `keepPreviousData` 로 프로젝트 전환 중에도 직전 프로젝트의 데이터를
 *   내려주므로, 이 훅은 그 구간을 `hasUnresolvedProjectSwitch` 로 구분해 노출하고
 *   `flatItems` 는 **항상 현재 선택된 프로젝트의 이벤트만** 담는다(전환 미해소 구간엔 빈 배열).
 *   필터 전환의 깜빡임 방지는 그대로 유지된다.
 */
export const useProjectHistory = () => {
  const {
    projectOptions,
    selectedProject,
    setSelectedProject,
    projectListRenderState,
    refetchProjects,
  } = useHistoryProjectSelection();

  const [historyTypeFilter, setHistoryTypeFilter] = useState<string | null>(
    null
  );

  const selectedProjectId = selectedProject
    ? Number(selectedProject)
    : undefined;

  const {
    data: historyData,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isPreviousData,
    refetch: refetchHistory,
  } = useGetProjectHistory({
    projectId: selectedProjectId,
    historyType: historyTypeFilter,
  });

  const hasUnresolvedProjectSwitch = useHasUnresolvedProjectSwitch({
    selectedProjectId,
    hasHistoryData: historyData !== undefined,
    isPreviousData,
    isHistoryError,
  });

  const flatItems: HistoryEvent[] = useMemo(
    () =>
      hasUnresolvedProjectSwitch
        ? []
        : historyData?.pages.flatMap((page) => page.items) ?? [],
    [historyData, hasUnresolvedProjectSwitch]
  );

  const isForbidden =
    isHistoryError &&
    historyError instanceof ProjectHistoryApiError &&
    historyError.status === 404;
  const isGeneralError = isHistoryError && !isForbidden;

  return {
    // 프로젝트 선택
    projectOptions,
    selectedProject,
    setSelectedProject,
    /**
     * 앞단 프로젝트 목록 조회의 렌더 갈래. 타임라인 조회와 별개다 — 이 조회가 실패하면
     * 뒤따르는 타임라인은 아예 시작하지 못하므로, "소속된 프로젝트가 없습니다" 대신
     * 실패와 재시도를 세워야 한다.
     */
    projectListRenderState,
    refetchProjects,

    // 이력 종류 필터 ("전체" === null)
    historyTypeFilter,
    setHistoryTypeFilter,

    // 타임라인
    flatItems,
    /**
     * 이 옵저버가 아직 표시할 성공 데이터를 하나도 갖지 못한 구간(react-query v4 `isLoading`).
     * - 프로젝트 미선택(쿼리 `enabled: false`) 구간도 **포함**한다 — 요청이 뜨지 않아도 true 다.
     * - `keepPreviousData` 로 직전 프로젝트·필터의 데이터가 유지되는 구간은 **제외**한다
     *   (그때 status 는 이미 success 라 false).
     * 따라서 `hasUnresolvedProjectSwitch` 와 배타적이지 않다 — 직전 프로젝트의 데이터가
     * 아직 없는 채로 프로젝트를 바꾸면 둘 다 true 다.
     */
    isHistoryLoading,
    /**
     * 현재 선택이 **기준 프로젝트**(마지막으로 자기 데이터가 도착한 프로젝트, 또는 아직
     * 도착 전이라면 최초 선택)와 다르고 그 결과가 아직 오류로도 확정되지 않은 구간 —
     * 즉 프로젝트 전환이 해소되지 않은 상태다(직전 프로젝트 데이터 유지 중이거나 아예 없음).
     * "선택된 프로젝트의 데이터 미도착"과는 다르다: 최초 선택은 도착 전에도 기준점이라
     * 최초 로딩 구간에서 false 이며, 그 구간은 `isHistoryLoading` 이 표현한다.
     * 조회가 오류로 확정되는 순간에도 false 로 바뀌며 그 구간은 `isForbidden`·`isGeneralError`
     * 가 표현한다.
     */
    hasUnresolvedProjectSwitch,
    isForbidden,
    isGeneralError,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetchHistory,
  };
};
